// src/products/dataroom/components/Modals/PermissionMatrixModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Save, FolderOpen, File, Eye, Printer, Download, Minus } from 'lucide-react';
import { supabase } from '../../../../config';

const PermissionMatrixModal = ({ isOpen, onClose, projectId, teams: teamsProp, onMessage }) => {
  const [step, setStep] = useState(1); // 1 = select dataroom, 2 = matrix
  const [datarooms, setDatarooms] = useState([]);
  const [selectedDatarooms, setSelectedDatarooms] = useState([]);
  const [currentDataroomIndex, setCurrentDataroomIndex] = useState(0);
  const [files, setFiles] = useState([]);
  const [teams, setTeams] = useState([]); // Teams for current dataroom
  const [permissions, setPermissions] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const permissionLevels = [
    { id: 'none', label: 'None', icon: Minus, color: 'gray' },
    { id: 'view', label: 'View', icon: Eye, color: 'blue' },
    { id: 'print', label: 'Print', icon: Printer, color: 'purple' },
    { id: 'download', label: 'Download', icon: Download, color: 'green' }
  ];

  const fetchDatarooms = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select('id, project_name, description')
        .order('project_name');

      if (error) throw error;
      setDatarooms(data || []);
      
      // Pre-select current project if provided
      if (projectId && data) {
        setSelectedDatarooms([projectId]);
      }
    } catch (error) {
      console.error('Error fetching datarooms:', error);
      onMessage && onMessage('Failed to load datarooms', 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId, onMessage]);

  // Fetch teams for a project
  const fetchTeams = useCallback(async (dataroomId) => {
    try {
      const { data: teamsData, error: teamsError } = await supabase
        .from('dataroom_teams')
        .select(`
          *,
          dataroom_parties!inner (
            id,
            name,
            project_id
          ),
          dataroom_user_teams(count)
        `)
        .eq('dataroom_parties.project_id', dataroomId)
        .order('name');

      if (teamsError) throw teamsError;
      setTeams(teamsData || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      // Fallback to teamsProp if available
      if (teamsProp && teamsProp.length > 0) {
        setTeams(teamsProp);
      }
    }
  }, [teamsProp]);

  const fetchFilesAndPermissions = useCallback(async (dataroomId) => {
    try {
      setLoading(true);
      
      // Fetch teams for this project first
      await fetchTeams(dataroomId);
      
      // Fetch files/folders
      const { data: filesData, error: filesError } = await supabase
        .from('dataroom_items')
        .select('*')
        .eq('project_id', dataroomId)
        .order('parent_id', { ascending: true })
        .order('name', { ascending: true });

      if (filesError) throw filesError;

      // Fetch existing permissions (Teams-based)
      const { data: permsData, error: permsError } = await supabase
        .from('dataroom_file_permissions')
        .select('*')
        .in('dataroom_item_id', filesData.map(f => f.id));

      if (permsError) throw permsError;

      setFiles(filesData || []);
      
      // Build permissions object: { fileId: { teamId: 'permission_level' } }
      const permsObj = {};
      (permsData || []).forEach(perm => {
        if (!permsObj[perm.dataroom_item_id]) {
          permsObj[perm.dataroom_item_id] = {};
        }
        permsObj[perm.dataroom_item_id][perm.team_id] = perm.permission_type;
      });
      setPermissions(permsObj);
    } catch (error) {
      console.error('Error fetching files:', error);
      onMessage && onMessage('Failed to load files', 'error');
    } finally {
      setLoading(false);
    }
  }, [onMessage, fetchTeams]);

  // Fetch available datarooms
  useEffect(() => {
    if (isOpen) {
      fetchDatarooms();
    }
  }, [isOpen, fetchDatarooms]);

  // Fetch files and existing permissions for current dataroom
  useEffect(() => {
    if (step === 2 && selectedDatarooms.length > 0) {
      fetchFilesAndPermissions(selectedDatarooms[currentDataroomIndex]);
    }
  }, [step, currentDataroomIndex, selectedDatarooms, fetchFilesAndPermissions]);

  // Toggle dataroom selection
  const toggleDataroom = (dataroomId) => {
    setSelectedDatarooms(prev => 
      prev.includes(dataroomId)
        ? prev.filter(id => id !== dataroomId)
        : [...prev, dataroomId]
    );
  };

  // Proceed to matrix view
  const proceedToMatrix = () => {
    if (selectedDatarooms.length === 0) {
      onMessage && onMessage('Please select at least one dataroom', 'error');
      return;
    }
    setStep(2);
    setCurrentDataroomIndex(0);
  };

  // Get permission for a file/team combination
  const getPermission = (fileId, teamId) => {
    return permissions[fileId]?.[teamId] || 'none';
  };

  // Set permission for a file/team combination
  const setPermission = (fileId, teamId, level) => {
    setPermissions(prev => ({
      ...prev,
      [fileId]: {
        ...(prev[fileId] || {}),
        [teamId]: level
      }
    }));
  };

  // Toggle file selection for bulk actions
  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Select all files
  const selectAllFiles = () => {
    setSelectedFiles(files.map(f => f.id));
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedFiles([]);
  };

  // Apply bulk permission
  const applyBulkPermission = (teamId, level) => {
    if (selectedFiles.length === 0) {
      onMessage && onMessage('Please select files first', 'error');
      return;
    }
    
    selectedFiles.forEach(fileId => {
      setPermission(fileId, teamId, level);
    });
    
    onMessage && onMessage(`Applied ${level} permission to ${selectedFiles.length} items`, 'success');
    clearSelection();
  };

  // Save permissions to database
  const savePermissions = async () => {
    try {
      setSaving(true);
      
      // Build upsert data (Teams-based)
      const upsertData = [];
      Object.keys(permissions).forEach(fileId => {
        Object.keys(permissions[fileId]).forEach(teamId => {
          const level = permissions[fileId][teamId];
          if (level && level !== 'none') {
            upsertData.push({
              team_id: parseInt(teamId),
              dataroom_item_id: parseInt(fileId),
              permission_type: level
            });
          }
        });
      });

      // Delete existing permissions for this dataroom first
      const fileIds = files.map(f => f.id);
      await supabase
        .from('dataroom_file_permissions')
        .delete()
        .in('dataroom_item_id', fileIds);

      // Insert new permissions
      if (upsertData.length > 0) {
        const { error } = await supabase
          .from('dataroom_file_permissions')
          .insert(upsertData);

        if (error) throw error;
      }

      onMessage && onMessage('Permissions saved successfully!', 'success');
      
      // Move to next dataroom or close
      if (currentDataroomIndex < selectedDatarooms.length - 1) {
        setCurrentDataroomIndex(prev => prev + 1);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      onMessage && onMessage('Failed to save permissions: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Build hierarchical tree structure
  const buildTree = (items, parentId = null) => {
    return items
      .filter(item => item.parent_id === parentId)
      .map(item => ({
        ...item,
        children: buildTree(items, item.id)
      }));
  };

  // Render file tree recursively
  const renderFileTreeColumn = (items, level = 0) => {
    return items.map(item => (
      <React.Fragment key={item.id}>
        <div className="hover:bg-gray-50 border-b border-gray-100">
          <div className="flex items-center px-4 py-2" style={{ paddingLeft: `${level * 20 + 16}px` }}>
            <input
              type="checkbox"
              checked={selectedFiles.includes(item.id)}
              onChange={() => toggleFileSelection(item.id)}
              className="mr-2 flex-shrink-0"
            />
            {item.is_folder ? (
              <FolderOpen className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
            ) : (
              <File className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
            )}
            <span className="text-sm font-medium text-gray-900 truncate">{item.name}</span>
          </div>
        </div>
        
        {/* Render children recursively */}
        {item.children && item.children.length > 0 && renderFileTreeColumn(item.children, level + 1)}
      </React.Fragment>
    ));
  };

  // Render permission columns for file tree
  const renderPermissionColumns = (items, level = 0) => {
    return items.map(item => (
      <React.Fragment key={item.id}>
        <tr className="hover:bg-gray-50 border-b border-gray-100">
          {/* Permission dropdowns for each team */}
          {teams.map(team => (
            <td key={team.id} className="px-2 py-2 text-center border-r border-gray-100">
              <select
                value={getPermission(item.id, team.id)}
                onChange={(e) => setPermission(item.id, team.id, e.target.value)}
                className={`text-xs px-2 py-1 rounded border w-full ${
                  getPermission(item.id, team.id) === 'download' ? 'bg-green-50 border-green-300 text-green-700' :
                  getPermission(item.id, team.id) === 'print' ? 'bg-purple-50 border-purple-300 text-purple-700' :
                  getPermission(item.id, team.id) === 'view' ? 'bg-blue-50 border-blue-300 text-blue-700' :
                  'bg-gray-50 border-gray-300 text-gray-600'
                }`}
              >
                {permissionLevels.map(level => (
                  <option key={level.id} value={level.id}>
                    {level.label}
                  </option>
                ))}
              </select>
            </td>
          ))}
        </tr>
        
        {/* Render children recursively */}
        {item.children && item.children.length > 0 && renderPermissionColumns(item.children, level + 1)}
      </React.Fragment>
    ));
  };

  if (!isOpen) return null;

  const currentDataroom = selectedDatarooms.length > 0 
    ? datarooms.find(d => d.id === selectedDatarooms[currentDataroomIndex])
    : null;
  const fileTree = buildTree(files);

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b p-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">
              {step === 1 ? 'Select Datarooms' : `Permissions Matrix: ${currentDataroom?.project_name || 'Loading...'}`}
            </h3>
            {step === 2 && (
              <p className="text-sm text-gray-600 mt-1">
                Dataroom {currentDataroomIndex + 1} of {selectedDatarooms.length}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {step === 1 ? (
            /* Step 1: Dataroom Selection */
            <div className="p-6 overflow-y-auto h-full">
              <p className="text-gray-600 mb-4">Select which dataroom(s) to configure permissions for:</p>
              
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {datarooms.map(dataroom => (
                    <label
                      key={dataroom.id}
                      className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDatarooms.includes(dataroom.id)}
                        onChange={() => toggleDataroom(dataroom.id)}
                        className="mr-3 h-4 w-4"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{dataroom.project_name}</div>
                        {dataroom.description && (
                          <div className="text-sm text-gray-500">{dataroom.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Step 2: Permission Matrix */
            <div className="flex flex-col h-full">
              {/* Bulk Actions Bar */}
              <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={selectAllFiles}
                      className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Select All
                    </button>
                    <button
                      onClick={clearSelection}
                      className="text-xs px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Clear ({selectedFiles.length})
                    </button>
                    
                    {selectedFiles.length > 0 && (
                      <>
                        <span className="text-sm text-gray-600">Apply to selected:</span>
                        {teams.map(team => (
                          <div key={team.id} className="flex items-center space-x-1">
                            <span className="text-xs text-gray-600">{team.name}:</span>
                            {permissionLevels.map(level => (
                              <button
                                key={level.id}
                                onClick={() => applyBulkPermission(team.id, level.id)}
                                className={`text-xs px-2 py-1 rounded ${
                                  level.id === 'download' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                                  level.id === 'print' ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' :
                                  level.id === 'view' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                                  'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                title={`Apply ${level.label} to ${team.name}`}
                              >
                                {level.label}
                              </button>
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Matrix Table */}
              <div className="flex-1 overflow-hidden p-6">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="flex h-full border rounded-lg overflow-hidden">
                    {/* Fixed Left Column - Files/Folders */}
                    <div className="w-80 flex-shrink-0 border-r bg-white overflow-y-auto">
                      <div className="sticky top-0 bg-gray-100 border-b z-10">
                        <div className="px-4 py-3 font-semibold text-sm text-gray-700">
                          Files/Folders
                        </div>
                      </div>
                      <div>
                        {fileTree.length > 0 ? (
                          renderFileTreeColumn(fileTree)
                        ) : (
                          <div className="text-center py-12 text-gray-500 text-sm">
                            No files or folders
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Scrollable Right Section - Teams */}
                    <div className="flex-1 overflow-x-auto overflow-y-auto">
                      <table className="w-full border-collapse">
                        <thead className="sticky top-0 bg-gray-100 z-10">
                          <tr>
                            {(teams.length > 0 ? teams : (teamsProp || [])).map(team => (
                              <th key={team.id} className="px-3 py-3 text-center text-sm font-semibold text-gray-700 border-r border-gray-200 min-w-[140px]">
                                <div className="font-medium">{team.name}</div>
                                {team.dataroom_parties && (
                                  <div className="text-xs font-normal text-gray-500 mt-1">
                                    {team.dataroom_parties.name}
                                  </div>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {fileTree.length > 0 ? (
                            renderPermissionColumns(fileTree)
                          ) : (
                            <tr>
                              <td colSpan={(teams.length > 0 ? teams : (teamsProp || [])).length} className="text-center py-12 text-gray-500">
                                No files or folders in this dataroom
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            {step === 2 && (
              <>
                <span className="text-sm text-gray-600">Legend:</span>
                <span className="inline-flex items-center text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                  🟢 Download
                </span>
                <span className="inline-flex items-center text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                  🟣 Print
                </span>
                <span className="inline-flex items-center text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                  🔵 View
                </span>
                <span className="inline-flex items-center text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                  ⚪ None
                </span>
              </>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {step === 2 && currentDataroomIndex > 0 && (
              <button
                onClick={() => setCurrentDataroomIndex(prev => prev - 1)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
            )}
            
            {step === 1 ? (
              <button
                onClick={proceedToMatrix}
                disabled={selectedDatarooms.length === 0}
                className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Next: Set Permissions
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={savePermissions}
                  disabled={saving}
                  className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {currentDataroomIndex < selectedDatarooms.length - 1 ? 'Save & Next' : 'Save & Close'}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PermissionMatrixModal;